/** T85 — Cài đặt thông báo. Real /api/v1/clinic/settings/notifications. */
import { apiClient } from "./client";
import { randomUuid } from "../randomUuid";

export interface NotificationSettings {
  emailEnabled: boolean;
  smsEnabled: boolean;
  zaloEnabled: boolean;
  remindPatient: boolean;
}

export const notificationSettingsApi = {
  get(): Promise<NotificationSettings> {
    return apiClient
      .get<NotificationSettings>("/v1/clinic/settings/notifications")
      .then((r) => r.data);
  },
  update(body: NotificationSettings): Promise<NotificationSettings> {
    return apiClient
      .put<NotificationSettings>("/v1/clinic/settings/notifications", body)
      .then((r) => r.data);
  },
};


/**
 * #18753 — per-event configuration. `suppliedParams` is what the clinical event can fill;
 * `missingParams` is what the currently-bound template still requires and the event cannot give.
 * A non-empty `missingParams` means the message would go out with blank fields, which Zalo accepts
 * with a 200 — so this screen is the only place it can be caught.
 */
export interface NotificationEventRow {
  eventKey: string;
  label: string;
  audience: "PATIENT" | "GUARDIAN" | "STAFF_ROLE";
  enabled: boolean;
  channelOrder: string[];
  templateCode: string | null;
  suppliedParams: string[];
  missingParams: string[];
  verifiedAt: string | null;
  /**
   * #18766 — for a staff-addressed event, the capability whose holders receive it when nobody
   * specific is responsible (e.g. `appointment.write`). A permission code, not a role name: roles
   * are per-clinic and renameable, capabilities are not. Null for patient events.
   */
  staffSelector: string | null;
  /**
   * #19180 — a system message (today: the patient OTP): sent by its own sender, configured
   * clinic-wide only, on its own fixed channels. The screen hides the per-service matrix. Since
   * #19612 its switch works like any other event's.
   */
  systemMessage: boolean;
  /** #19180 — the channels this event may use, in order. The OTP is ZBS → SMS, never e-mail. */
  allowedChannels: string[];
  /**
   * #19209 — whether this event can actually go out on Zalo right now. Null for staff events.
   * `UNKNOWN` means the template catalogue could not be read, not that anything is wrong.
   */
  zbsReadiness?: ZbsReadiness | null;
  /** #19209 — the old `HIS_*` placeholder. Inherited, it is not a template the clinic chose. */
  builtInTemplateCode?: string | null;
  /**
   * Every field a template bound to this event may ask for: what the event supplies, the provider
   * names it fills directly, and the `clinic.*` fields Addy Message fills itself. Absent on an
   * older API, in which case the picker does not pre-judge templates.
   */
  fillableParams?: string[];
  /**
   * #19564 — what a template bound to this event must carry: any ONE group, in full. Absent on an
   * older API, in which case the picker does not pre-judge templates on it.
   */
  essentialFields?: string[][];
  /** #19564 — why the clinic-wide Zalo template is `TEMPLATE_UNSUITABLE`, in Vietnamese. */
  unsuitableReason?: string | null;
  /** #19566 — readiness of every channel the event may use, clinic-wide. Absent on older APIs. */
  channelReadiness?: Record<string, ZbsReadiness> | null;
}

/** #19566 — one (event, channel)'s delivery outcomes over the window: counts and codes only. */
export interface DeliveryStat {
  eventKey: string;
  channel: string;
  counts: Record<string, number>;
  failures: { code: string; count: number }[];
}

export type ZbsReadiness =
  | "READY"
  | "DISABLED"
  | "CHANNEL_OFF"
  | "NO_TEMPLATE"
  | "TEMPLATE_NOT_FOUND"
  | "TEMPLATE_ARCHIVED"
  | "TEMPLATE_NOT_APPROVED"
  | "TEMPLATE_UNSUITABLE"
  | "PARAMS_MISSING"
  | "UNKNOWN";

export interface MessagingTemplateParam {
  key: string;
  /** The canonical field the platform fills this parameter from (#18935); the key when unmapped. */
  canonicalKey?: string;
  position: number;
  required: boolean;
  maxLength: number | null;
  /** Zalo's own example value (Addy Message `rendition_params.sample_value`); absent on older builds. */
  sampleValue?: string | null;
}

export interface MessagingTemplateRendition {
  channel: string;
  approvalStatus: string;
  providerTemplateId: string | null;
  unitPrice: number | null;
  previewImageUrl: string | null;
  syncedAt: string | null;
  params: MessagingTemplateParam[];
  /**
   * The template's name at the provider ("Nhắc lịch hẹn khám"), for people to read; the code stays
   * the identifier (Addy Message #19571). Absent on older builds.
   */
  providerTemplateName?: string | null;
  /** #18935 — the canonical fields this rendition needs (`otp.code`, `patient.name`…). */
  canonicalParams?: string[];
}

export interface MessagingTemplate {
  code: string;
  category: number;
  status: number;
  renditions: MessagingTemplateRendition[];
  /** Addy Message D-130 — archived templates are refused at send time. Absent on older builds. */
  archived?: boolean;
}

export interface NotificationEventUpdate {
  enabled: boolean;
  channelOrder: string[];
  templateCode: string | null;
  boundParams: string[] | null;
}

export const notificationEventsApi = {
  list(): Promise<NotificationEventRow[]> {
    return apiClient
      .get<NotificationEventRow[]>("/v1/clinic/settings/notifications/events")
      .then((r) => r.data);
  },
  deliveryStats(days = 7): Promise<DeliveryStat[]> {
    return apiClient
      .get<DeliveryStat[]>("/v1/clinic/settings/notifications/events/delivery-stats", {
        params: { days },
      })
      .then((r) => r.data);
  },
  templates(): Promise<MessagingTemplate[]> {
    return apiClient
      .get<MessagingTemplate[]>("/v1/clinic/settings/notifications/events/templates")
      .then((r) => r.data);
  },
  update(eventKey: string, body: NotificationEventUpdate): Promise<NotificationEventRow> {
    return apiClient
      .put<NotificationEventRow>(
        `/v1/clinic/settings/notifications/events/${eventKey}`,
        body,
      )
      .then((r) => r.data);
  },
};

/** One cell of the per-service matrix (#18933). `serviceId: null` is the clinic-wide default. */
export interface NotificationCell {
  serviceId: string | null;
  channel: string;
  enabled: boolean;
  templateCode: string | null;
  /** True when nothing is stored here and the value shown came from a wider scope. */
  inherited: boolean;
  source: string;
  /**
   * #19554 — the clinic's fixed values for this cell's template parameters that no event fills
   * (e.g. `_TY_LE_GIAM_GIA_CT_KM_`), keyed by the parameter's own name. Absent on an older API.
   */
  staticParams?: Record<string, string>;
  /** #19566 — whether this cell can go out, with the event row's rule. Absent on older APIs. */
  readiness?: ZbsReadiness | null;
}

export interface NotificationCellUpdate {
  serviceId: string | null;
  channel: string;
  enabled: boolean;
  templateCode: string | null;
  boundParams: string[] | null;
  /** #19554 — fixed values for the template's unmapped parameters; the server keeps only those. */
  staticParams?: Record<string, string> | null;
  /**
   * #19565 — fixed values the clinic confirmed are meant to be identical in every message although
   * they look like per-visit data (a date, a time, Zalo's example). The server refuses those
   * without it.
   */
  acknowledgedStaticParams?: string[] | null;
}

export const notificationCellsApi = {
  /** #19667 D8 — every patient event's cells in one request, keyed by event. */
  listAll(): Promise<Record<string, NotificationCell[]>> {
    return apiClient
      .get<Record<string, NotificationCell[]>>("/v1/clinic/settings/notifications/events/cells")
      .then((r) => r.data);
  },
  list(eventKey: string): Promise<NotificationCell[]> {
    return apiClient
      .get<NotificationCell[]>(`/v1/clinic/settings/notifications/events/${eventKey}/cells`)
      .then((r) => r.data);
  },
  save(eventKey: string, body: NotificationCellUpdate): Promise<void> {
    return apiClient
      .put(`/v1/clinic/settings/notifications/events/${eventKey}/cells`, body)
      .then(() => undefined);
  },
  /** #19581 — drop a service's own row, every channel; it goes back to the clinic default. */
  removeService(eventKey: string, serviceId: string): Promise<void> {
    return apiClient
      .delete(`/v1/clinic/settings/notifications/events/${eventKey}/cells`, {
        params: { serviceId },
      })
      .then(() => undefined);
  },
};

/**
 * #18935 — the variable catalogue behind /settings/notification-variables.
 *
 * Two owners, one row. This system says which events can supply a field
 * (`events`, `suppliedByThisSystem`); Addy Message says how it is written —
 * the Vietnamese label, the example, and above all `aliases`, the older
 * `_TEN_KHACH_HANG_` spellings that exist because Zalo templates named
 * parameters that way. That last part is a delivery detail, not a clinical
 * one, which is why this side has deliberately never held it.
 */
export interface NotificationVariableRow {
  key: string;
  object: string;
  label: string;
  description: string;
  type: string;
  example: string;
  aliases: string[];
  channels: string[];
  events: string[];
  templateUsage: number;
  suppliedByThisSystem: boolean;
}

export const notificationVariablesApi = {
  list(): Promise<NotificationVariableRow[]> {
    return apiClient
      .get<NotificationVariableRow[]>("/v1/clinic/settings/notifications/events/variables")
      .then((r) => r.data);
  },
};

/** #19567 — one place a template is used; `channel` null is the event-level fallback template. */
export interface TemplateUsage {
  eventKey: string;
  eventLabel: string;
  serviceId: string | null;
  channel: string | null;
  enabled: boolean;
}

/** #19567 — why the replacement cannot go to one place. */
export interface ReplaceProblem {
  eventKey: string;
  eventLabel: string;
  serviceId: string | null;
  channel: string | null;
  problem: string;
}

export const notificationTemplateToolsApi = {
  usages(code: string): Promise<TemplateUsage[]> {
    return apiClient
      .get<TemplateUsage[]>(
        `/v1/clinic/settings/notifications/events/templates/${encodeURIComponent(code)}/usages`,
      )
      .then((r) => r.data);
  },
  /** All or nothing; a refusal (422) carries `problems` listing every place that blocked it. */
  replace(fromCode: string, toCode: string): Promise<{ replaced: number }> {
    return apiClient
      .post<{ replaced: number }>(
        "/v1/clinic/settings/notifications/events/templates/replace",
        { fromCode, toCode },
        { headers: { "Idempotency-Key": randomUuid() } },
      )
      .then((r) => r.data);
  },
  /**
   * Example data to the signed-in user's own phone (Zalo test mode) or own e-mail address (a real
   * letter through the clinic's SMTP server, #19641).
   */
  testSend(
    eventKey: string,
    body: { channel: string; serviceId: string | null },
  ): Promise<{ sentTo: string; templateCode: string; platformMessageId: string | null }> {
    return apiClient
      .post<{ sentTo: string; templateCode: string; platformMessageId: string | null }>(
        `/v1/clinic/settings/notifications/events/${eventKey}/test-send`,
        body,
        { headers: { "Idempotency-Key": randomUuid() } },
      )
      .then((r) => r.data);
  },
};

/**
 * #19584 — what a ZBS send from one cell would carry, computed by Addy Message's own send rules.
 * `sources` maps each Zalo parameter to the field its value came from (`appointment.date`) or to
 * itself when it is a fixed value; a parameter with no value is in `missing`.
 */
export interface TemplatePreview {
  templateCode: string;
  channel: string;
  providerTemplateName?: string | null;
  previewImageUrl?: string | null;
  providerParams: Record<string, unknown>;
  sources: Record<string, string>;
  missing: string[];
  blank?: string[];
  tooLong?: string[];
  /** Field → Vietnamese label, for every field a value came from. */
  sourceLabels: Record<string, string>;
  fixedParams: string[];
  /** True when this preview is of an unsaved draft. */
  draft: boolean;
  /** #19641 — EMAIL only: the letter as the send would build it, example data filled in. */
  subject?: string | null;
  bodyHtml?: string | null;
  bodyText?: string | null;
  /** #19667 — SMS only: how the carriers bill the text. */
  smsEncoding?: "GSM7" | "UCS2" | string | null;
  smsUnits?: number | null;
  smsSegments?: number | null;
}

export interface TemplatePreviewRequest {
  channel: string;
  serviceId?: string | null;
  /** A template being chosen; omitted = the stored one. */
  templateCode?: string | null;
  /** Fixed values being typed; omitted = the stored ones. */
  staticParams?: Record<string, string> | null;
}

export const notificationPreviewApi = {
  preview(eventKey: string, body: TemplatePreviewRequest): Promise<TemplatePreview> {
    return apiClient
      .post<TemplatePreview>(
        `/v1/clinic/settings/notifications/events/${eventKey}/preview`,
        body,
      )
      .then((r) => r.data);
  },
  /** #19605 — Zalo's preview page, fetched by HIS so the frame can be sized to it. */
  zaloPage(url: string): Promise<string> {
    return apiClient
      .get<{ html: string }>(`/v1/clinic/settings/notifications/zalo-preview-page`, {
        params: { url },
      })
      .then((r) => r.data.html);
  },
};

export const notificationSettingsQueryKeys = {
  channels: () => ["clinic-messaging-channels"] as const,
  preview: (eventKey: string, body: TemplatePreviewRequest) =>
    ["clinic-notification-preview", eventKey, body] as const,
  all: () => ["clinic-notification-settings"] as const,
  events: () => ["clinic-notification-events"] as const,
  templates: () => ["clinic-notification-templates"] as const,
  cells: (eventKey: string) => ["clinic-notification-cells", eventKey] as const,
  // Under the per-event prefix, so invalidating "clinic-notification-cells" covers it (#19667 review).
  allCells: () => ["clinic-notification-cells", "__all"] as const,
  variables: () => ["clinic-notification-variables"] as const,
  deliveryStats: (days: number) => ["clinic-notification-delivery-stats", days] as const,
  usages: (code: string) => ["clinic-notification-template-usages", code] as const,
};

/**
 * #19618 — can the clinic send on this channel at all, as Addy Message reports it. `UNKNOWN` means
 * Addy Message could not be asked — never render it as "not connected".
 */
export type ChannelConnection = "OK" | "OFF" | "UNKNOWN";

export interface MessagingChannelLine {
  channel: "ZBS" | "SMS" | "EMAIL" | string;
  state: ChannelConnection;
  /** Approved templates Addy Message would send on this channel; absent when unknown. */
  sendableTemplates?: number | null;
  /** Vietnamese, for the clinic: what is missing on this channel. */
  problem?: string | null;
}

export interface MessagingChannels {
  reachable: boolean;
  /** Set when the clinic has no Addy Message tenant at all — only a platform admin can fix it. */
  actionable?: string | null;
  channels: MessagingChannelLine[];
}

export const messagingChannelsApi = {
  get(): Promise<MessagingChannels> {
    return apiClient
      .get<MessagingChannels>("/v1/clinic/messaging/channels")
      .then((r) => r.data);
  },
};
